import { LayoutGrid } from "lucide-react";

import { ControlButton } from "../common/ControlButton";
import { TrackedDropdownMenu } from "../common/TrackedDropdownMenu";
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/clientWeb/components/ui/dropdown-menu";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";

interface AppsMenuProps {
  onOpenWhiteboard?: () => void;
  onOpenBreakoutRooms?: () => void;
  /** Viewer may configure breakout rooms. */
  canManageBreakout?: boolean;
  /** They are already running, so there is nothing to configure. */
  breakoutStarted?: boolean;
  /** Somewhere to go, and not there yet — what makes Join worth offering. */
  breakoutJoinPending?: boolean;
  /** Reopen the join prompt. The way back in for anybody who dismissed it. */
  onOpenBreakoutJoin?: () => void;
  onOpenPolling?: () => void;
  onOpenDocumentShare?: () => void;
  onOpenVideoShare?: () => void;
  /** A request to present is waiting for the host: these say so instead of opening. */
  pendingRequestLabel?: string;
  onOpenLiveStreaming?: () => void;
  onOpenTranscription?: () => void;
}

export function AppsMenu({
  onOpenWhiteboard,
  onOpenBreakoutRooms,
  canManageBreakout = false,
  breakoutStarted = false,
  breakoutJoinPending = false,
  onOpenBreakoutJoin,
  onOpenPolling,
  onOpenDocumentShare,
  onOpenVideoShare,
  pendingRequestLabel,
  onOpenLiveStreaming,
  onOpenTranscription,
}: AppsMenuProps) {
  const t = useTranslation();
  return (
    <TrackedDropdownMenu>
      <DropdownMenuTrigger asChild>
        <div>
          <ControlButton onClick={() => { }} label={t("controlBar.apps")}>
            <LayoutGrid className="h-5 w-5 text-white" />
          </ControlButton>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="min-w-[200px]">
        <DropdownMenuItem className="gap-3" disabled={!!pendingRequestLabel} title={pendingRequestLabel} onSelect={() => onOpenWhiteboard?.()}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span>{t("controlBar.startWhiteBoard")}</span>
        </DropdownMenuItem>
        {/* One Breakout Rooms item, meaning whichever thing there is left to
            do. For a participant once the rooms are running, that is getting
            back to the prompt they dismissed. For a moderator it is the
            configuration — which stays reachable here for as long as the rooms
            exist, including from inside one. Running the rooms is not
            something they stop being able to do by walking into a room, and
            this menu is one of the places they look for it. */}
        {/* Only while there is a room to go TO. Once they are sitting in the
            room they were assigned, Join is an offer to do what they have
            already done — it reappears by itself if the host moves them
            somewhere else. */}
        {breakoutStarted && !canManageBreakout && breakoutJoinPending && onOpenBreakoutJoin && (
          <DropdownMenuItem className="gap-3" onSelect={() => onOpenBreakoutJoin()}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span>{t("controlBar.joinRoom")}</span>
          </DropdownMenuItem>
        )}

        {canManageBreakout && (
        <DropdownMenuItem className="gap-3" onSelect={() => onOpenBreakoutRooms?.()}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <span>{t("controlBar.breakoutRooms")}</span>
        </DropdownMenuItem>
        )}
        <DropdownMenuItem className="gap-3" onSelect={() => onOpenPolling?.()}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span>{t("controlBar.polling")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-3" disabled={!!pendingRequestLabel} title={pendingRequestLabel} onSelect={() => onOpenDocumentShare?.()}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>{t("controlBar.documentShare")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-3" disabled={!!pendingRequestLabel} title={pendingRequestLabel} onSelect={() => onOpenVideoShare?.()}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span>{t("controlBar.videoShare")}</span>
        </DropdownMenuItem>
        {/* Live streaming is the host's. The caller drops the handler for
            everyone else, and an item that opens nothing is worse than no item
            — same idiom as the cloud-recording entry below. */}
        {onOpenLiveStreaming && (
          <DropdownMenuItem className="gap-3" onSelect={() => onOpenLiveStreaming()}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span>{t("controlBar.liveStreaming")}</span>
          </DropdownMenuItem>
        )}
        {/* Only surface "View Transcription" when transcription is actually
            enabled for this session — otherwise the panel would be empty. */}
        {onOpenTranscription && (
          <DropdownMenuItem className="gap-3" onSelect={() => onOpenTranscription()}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{t("controlBar.viewTranscription")}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </TrackedDropdownMenu>
  );
}
